var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, observable, makeObservable } from 'mobx';
import { MandateQueryType, MandateType } from '../config/mandate';
import CustomerMandate from '../config/mandate/CustomerMandate';
import PersonMandate from '../config/mandate/PersonMandate';
import API from '../util/ApiUtil';
import { add, endOfDay } from 'date-fns';
export const DEFAULT_VALID_UNTIL = endOfDay(add(new Date(), { years: 3 }));
export default class MandateStore {
    constructor(rootStore) {
        Object.defineProperty(this, "resultMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "customerId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ssoStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isRemovePersonMandateDisabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personMandate) => {
                return (!this.hasMandate([MandateType.LEGAL], true) ||
                    personMandate.mandates.some((mandate) => mandate.type === MandateType.LEGAL && mandate.source !== 'NOTARIAL'));
            }
        });
        Object.defineProperty(this, "isManageCustomerMandateDisabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (customerMandate) => {
                return !this.hasMandate([MandateType.LEGAL]) || customerMandate.mandates.some((mandate) => mandate.type === MandateType.LEGAL);
            }
        });
        Object.defineProperty(this, "hasMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandates, matchAll = false) => {
                return this.getMandateResult(mandates, matchAll).value;
            }
        });
        Object.defineProperty(this, "isFetched", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandates, matchAll = false) => {
                return this.getMandateResult(mandates, matchAll).status === 'FETCHED';
            }
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => {
                return this.resultMap.get(key) && [undefined, 'FETCHING'].includes(this.resultMap.get(key).status);
            }
        });
        Object.defineProperty(this, "hasError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => {
                return this.resultMap.get(key) && ['ERROR'].includes(this.resultMap.get(key).status);
            }
        });
        Object.defineProperty(this, "getResultValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => {
                return this.resultMap.get(key) ? this.resultMap.get(key).value : [];
            }
        });
        Object.defineProperty(this, "removePersonMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (personMandate) => {
                this.onFetchStart(MandateQueryType.REMOVE_PERSON_MANDATE, false);
                return API.put('/mandate/person-mandate', { ...personMandate, mandates: this.getMandatesWithRemovedTimestamp(personMandate) })
                    .then(() => this.onRemoveSuccess(personMandate))
                    .catch((response) => this.onFetchError(response, MandateQueryType.REMOVE_PERSON_MANDATE));
            }
        });
        Object.defineProperty(this, "getMandatesWithRemovedTimestamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personMandate) => {
                const mandates = [];
                personMandate.mandates.forEach((mandate) => {
                    if (mandate.isLegal() || mandate.isDiilMandate()) {
                        mandates.push({ ...mandate, validUntil: new Date() });
                    }
                    else {
                        mandates.push({ ...mandate });
                    }
                });
                return mandates;
            }
        });
        Object.defineProperty(this, "onRemoveSuccess", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personMandate) => {
                this.resultMap.set(MandateQueryType.REMOVE_PERSON_MANDATE, { status: 'OK' });
                this.removePersonMandateFromResultMap(personMandate);
            }
        });
        Object.defineProperty(this, "removePersonMandateFromResultMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personMandate) => {
                this.resultMap.get(MandateQueryType.PERSON_MANDATES).value = this.resultMap
                    .get(MandateQueryType.PERSON_MANDATES)
                    .value.filter((personMandateResult) => personMandateResult.redisId !== personMandate.redisId);
            }
        });
        Object.defineProperty(this, "fetchMandates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (key) => {
                this.onFetchStart(key, []);
                return API.get(`/mandate/${key}`)
                    .then((response) => this.onFetchSuccess(response, key))
                    .catch((response) => this.onFetchError(response, key));
            }
        });
        Object.defineProperty(this, "onFetchSuccess", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (response, key) => {
                this.resultMap.get(key).value = this.isMandateQueryType(key) ? this.convertToMandateType(key, response) : response.data;
                this.resultMap.get(key).status = 'FETCHED';
            }
        });
        Object.defineProperty(this, "isMandateQueryType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => {
                return [MandateQueryType.PERSON_MANDATES, MandateQueryType.CUSTOMER_MANDATES].includes(key);
            }
        });
        Object.defineProperty(this, "convertToMandateType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key, response) => {
                return response.data.map((mandate) => (key === MandateQueryType.CUSTOMER_MANDATES ? new CustomerMandate(mandate) : new PersonMandate(mandate)));
            }
        });
        Object.defineProperty(this, "onFetchStart", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key, value) => {
                this.resultMap.set(key, { status: 'FETCHING', value });
            }
        });
        Object.defineProperty(this, "onFetchError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e, key) => {
                this.resultMap.get(key).status = 'ERROR';
                console.error(e);
            }
        });
        makeObservable(this);
        this.ssoStore = rootStore.ssoStore;
    }
    async loadMandateInfo(mandates, matchAll = false) {
        this.handleCustomerChange();
        if (this.noNeedToLoadData(mandates, matchAll)) {
            return Promise.resolve();
        }
        return this.fetchMandateInfo(mandates, matchAll);
    }
    fetchMandateInfo(mandates, matchAll = false) {
        const key = MandateStore.getKey(mandates, matchAll);
        this.onFetchStart(key, false);
        return API.get(`/mandate/${matchAll ? 'hasAllMandates' : 'hasAnyMandate'}`, { params: { mandates } })
            .then((response) => this.onFetchSuccess(response, key))
            .catch((response) => this.onFetchError(response, key));
    }
    handleCustomerChange() {
        if (this.customerId !== this.ssoStore.customerId) {
            this.customerId = this.ssoStore.customerId;
            this.resultMap.clear();
        }
    }
    noNeedToLoadData(mandates, matchAll = false) {
        return !this.customerId || ['FETCHING', 'FETCHED'].includes(this.getMandateResult(mandates, matchAll).status);
    }
    getMandateResult(mandates, matchAll = false) {
        return this.resultMap.get(MandateStore.getKey(mandates, matchAll)) || { status: 'UNINITIALIZED' };
    }
    static getKey(mandates, matchAll = false) {
        return `${mandates.sort().join('_')}_${matchAll}`;
    }
}
__decorate([
    observable
], MandateStore.prototype, "resultMap", void 0);
__decorate([
    observable
], MandateStore.prototype, "customerId", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "fetchMandateInfo", null);
__decorate([
    action.bound
], MandateStore.prototype, "removePersonMandate", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "onRemoveSuccess", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "removePersonMandateFromResultMap", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "fetchMandates", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "onFetchSuccess", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "onFetchStart", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "onFetchError", void 0);
__decorate([
    action.bound
], MandateStore.prototype, "handleCustomerChange", null);
